// src/pages/ContentEditor/TextEditor.js
import React, { useRef, useEffect } from "react";

const TextEditor = React.forwardRef(({ content, setContent, setImages, placeholder }, ref) => {
  const editorRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    editorRef: editorRef,
  }));

  useEffect(() => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const adjustHeight = () => {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.max(textarea.scrollHeight, 400)}px`;
    };

    adjustHeight();
    textarea.addEventListener("input", adjustHeight);

    return () => {
      textarea.removeEventListener("input", adjustHeight);
    };
  }, [content]);

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") === -1) continue;

      e.preventDefault();

      const blob = item.getAsFile();
      const filename = `pasted-image-${Date.now()}.${blob.type.split("/")[1]}`;
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => ({
          ...prev,
          [filename]: event.target.result,
        }));
        setContent((prevContent) => {
          const selStart = editorRef.current.selectionStart;
          return (
            prevContent.substring(0, selStart) +
            `\n[Image: ${filename}]\n` +
            prevContent.substring(selStart)
          );
        });
      };
      reader.readAsDataURL(blob);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith("image/")) continue;

      const filename = file.name;
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => ({
          ...prev,
          [filename]: event.target.result,
        }));
        setContent((prevContent) => {
          const selStart = editorRef.current.selectionStart;
          return (
            prevContent.substring(0, selStart) +
            `\n[Image: ${filename}]\n` +
            prevContent.substring(selStart)
          );
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Restore missing handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    editorRef.current.classList.add("border-blue-500");
  };

  const handleDragLeave = () => {
    editorRef.current.classList.remove("border-blue-500");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;

      setContent(
        content.substring(0, start) + "  " + content.substring(end)
      );

      setTimeout(() => {
        e.target.selectionStart = start + 2;
        e.target.selectionEnd = start + 2;
      }, 0);
    }
  };

  return (
    <textarea
      ref={editorRef}
      value={content}
      onChange={(e) => setContent(e.target.value)}
      onPaste={handlePaste}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="w-full p-4 h-full focus:outline-none transition-all font-mono text-gray-700 resize-none border-0 whitespace-pre-wrap word-break-break-word"
      style={{
        maxWidth: "100%",
        overflowWrap: "break-word",
        wordWrap: "break-word",
        wordBreak: "break-word",
        WebkitHyphens: "auto",
        MsHyphens: "auto",
        hyphens: "auto",
      }}
      spellCheck="true"
    />
  );
});

export default TextEditor;